import type { ReactNode } from 'react';
import { CheckCircle2, FileEdit, Send, Undo2 } from 'lucide-react';
import { StatusPill, type StatusPillStatus } from '../../components/ui';
import { cn } from '../../lib/cn';
import type { PeriodKind, Timesheet, TimesheetRange, TimesheetStatus } from './api';

/**
 * The vocabulary of week submission, in ONE place.
 *
 * Four states appear on three screens (week header, day header, approvals
 * roster). Spelling "Awaiting approval" three times in three colours is how a
 * user ends up unsure whether "Submitted" and "Awaiting approval" are the same
 * thing — so the label, the colour and the glyph are decided here and nowhere
 * else.
 */

export interface StatusMeta {
  pill: StatusPillStatus;
  /** The label on the pill — short, because it sits in a header. */
  label: string;
  icon: ReactNode;
  /** Plain-language meaning, used in tooltips and the roster legend. */
  meaning: string;
}

export const STATUS_META: Record<TimesheetStatus, StatusMeta> = {
  DRAFT: {
    pill: 'draft',
    label: 'Draft',
    icon: <FileEdit className="h-3.5 w-3.5" aria-hidden />,
    meaning: 'Still being tracked — nobody has sent it in yet.',
  },
  SUBMITTED: {
    pill: 'open',
    label: 'Awaiting approval',
    icon: <Send className="h-3.5 w-3.5" aria-hidden />,
    meaning: 'Sent in and waiting for an approver. The hours are read-only (an admin may still correct them).',
  },
  APPROVED: {
    pill: 'paid',
    label: 'Approved',
    icon: <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />,
    meaning: 'Signed off. The hours are read-only until an approver reopens the period.',
  },
  REJECTED: {
    pill: 'late',
    label: 'Changes requested',
    icon: <Undo2 className="h-3.5 w-3.5" aria-hidden />,
    meaning: 'Sent back with a note. Fix the period and submit it again.',
  },
};

export function TimesheetStatusPill({
  status,
  className,
}: {
  status: TimesheetStatus;
  className?: string;
}) {
  const meta = STATUS_META[status];
  return (
    <StatusPill status={meta.pill} label={meta.label} className={className} />
  );
}

/**
 * "Fri, Aug 14 at 4:12 PM" — a review timestamp.
 *
 * Approvals are a conversation between two people about one week, and "who,
 * when" is the whole audit trail. A bare date would leave two submissions on
 * the same day indistinguishable.
 */
export function formatStamp(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** "Aug 14" — the compact form for a dense roster column. */
export function formatStampShort(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
}

/**
 * The one-line history under the pill: who did what to this week, and when.
 *
 * A status with no provenance is an accusation without a name — "Changes
 * requested" is useless to the person who has to act on it unless it says who
 * asked and when.
 */
export function timesheetProvenance(sheet: Timesheet): string | null {
  const reviewer = sheet.reviewedBy?.name ?? 'an approver';
  switch (sheet.status) {
    case 'SUBMITTED': {
      const when = formatStamp(sheet.submittedAt);
      return when ? `Submitted ${when}` : 'Submitted';
    }
    case 'APPROVED': {
      const when = formatStamp(sheet.reviewedAt);
      return `Approved by ${reviewer}${when ? ` · ${when}` : ''}`;
    }
    case 'REJECTED': {
      const when = formatStamp(sheet.reviewedAt);
      return `Sent back by ${reviewer}${when ? ` · ${when}` : ''}`;
    }
    default:
      // A DRAFT that carries a reviewer was REOPENED — the owner is entitled to
      // know an approved week was unlocked, and by whom.
      if (sheet.reviewedBy && sheet.reviewedAt) {
        return `Reopened by ${reviewer} · ${formatStamp(sheet.reviewedAt)}`;
      }
      return null;
  }
}

/**
 * A note written by one person for another to read.
 *
 * Rendered as a quotation with an attribution rather than as body copy: a
 * rejection reason that looks like UI chrome gets skimmed past, and the whole
 * point of sending a week back is that the reason is read.
 */
export function TimesheetNote({
  note,
  author,
  tone = 'neutral',
  className,
}: {
  note: string;
  author: string;
  tone?: 'neutral' | 'warning';
  className?: string;
}) {
  return (
    <blockquote
      className={cn(
        'rounded-lg border-l-2 px-3 py-1.5 text-body-sm leading-snug',
        tone === 'warning'
          ? 'border-l-error bg-error/[0.06] text-secondary'
          : 'border-l-grey-300 bg-grey-50 text-grey-600',
        className,
      )}
    >
      <span className="italic">“{note}”</span>
      <cite className="ml-1.5 not-italic text-caption text-grey-500">— {author}</cite>
    </blockquote>
  );
}


// ---------------------------------------------------------------------------
// Periods — a timesheet is a DAY, a WEEK or a MONTH (the person's cadence).
// The screens still VIEW a week, so a week on screen can sit in two periods
// (Aug 31 – Sep 6 for a monthly person) and be half locked.
// ---------------------------------------------------------------------------

/** "the week of Aug 10 – 16, 2026" · "August 2026" · "Tue, Sep 29, 2026". */
export function periodNoun(sheet: { periodKind: PeriodKind; label: string }): string {
  return sheet.periodKind === 'WEEK' ? `the week of ${sheet.label}` : sheet.label;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The label on a period's Submit button — it names what is being sent in. */
export function submitLabel(sheet: { periodKind: PeriodKind; label: string; status: TimesheetStatus }): string {
  const verb = sheet.status === 'REJECTED' ? 'Resubmit' : 'Submit';
  if (sheet.periodKind === 'WEEK') return `${verb} week`;
  if (sheet.periodKind === 'DAY') return `${verb} ${shortDay(sheet.label)}`;
  return `${verb} ${sheet.label}`;
}

/** "Tue, Sep 29, 2026" → "Tue, Sep 29" — a button has no room for the year. */
function shortDay(label: string): string {
  return label.replace(/, \d{4}$/, '');
}

/** "Sep 30" from YYYY-MM-DD, read as a calendar date (never through UTC). */
export function shortIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** The sentence a read-only day carries: which period, which lock, what undoes it. */
export function reviewLockMessage(sheet: {
  periodKind: PeriodKind;
  label: string;
  status: TimesheetStatus;
}): string {
  const noun = capitalise(periodNoun(sheet));
  return sheet.status === 'APPROVED'
    ? `${noun} is approved and read-only. An approver has to reopen it before these hours can change.`
    : `${noun} is submitted for approval and read-only. Withdraw the submission to edit it.`;
}

export interface DayReview {
  /** The review-lock sentence when this VIEWER may not write the day, else null. */
  lock: string | null;
  status: TimesheetStatus;
  periodKind: PeriodKind;
  periodStart: string;
  label: string;
  /**
   * An ADMIN on a SUBMITTED period: the server takes their writes (the admin
   * correction), so the day is editable — and must look like a correction.
   */
  correcting: boolean;
}

/**
 * ONE ANSWER to "may this viewer write this day?". Lock a DAY by its governing
 * period (`dayStatus[date]`), never the whole week by `timesheet.locked`.
 * Admins may correct a SUBMITTED period (never an APPROVED one) — the same
 * rule `TimesheetService.assertDaysOpen({allowSubmitted})` enforces.
 */
export function dayReview(range: TimesheetRange, date: string, isAdmin: boolean): DayReview {
  const ds = range.dayStatus?.[date] ?? {
    status: range.timesheet.status,
    locked: range.timesheet.locked,
    periodKind: range.timesheet.periodKind,
    periodStart: range.timesheet.periodStart,
    label: range.timesheet.label,
  };
  const correcting = isAdmin && ds.status === 'SUBMITTED';
  return {
    lock: ds.locked && !correcting ? reviewLockMessage(ds) : null,
    status: ds.status,
    periodKind: ds.periodKind,
    periodStart: ds.periodStart,
    label: ds.label,
    correcting,
  };
}

/**
 * The periods that GOVERN some day of the range, in order — the ones a header
 * has to describe. History of another kind that governs nothing is dropped.
 */
export function periodsInView(range: TimesheetRange, onlyDate?: string): Timesheet[] {
  const days = Object.entries(range.dayStatus ?? {})
    .filter(([date]) => !onlyDate || date === onlyDate)
    .map(([, ds]) => ds);
  const governs = (p: Timesheet) =>
    days.some((ds) => ds.periodKind === p.periodKind && ds.periodStart === p.periodStart);
  const list = (range.periods ?? []).filter(governs);
  return list.length > 0 ? list : [range.timesheet];
}

/** Days of `range` a period governs (YYYY-MM-DD, ascending). */
export function daysGovernedBy(range: TimesheetRange, sheet: Timesheet): string[] {
  return Object.entries(range.dayStatus ?? {})
    .filter(([, ds]) => ds.periodKind === sheet.periodKind && ds.periodStart === sheet.periodStart)
    .map(([date]) => date)
    .sort();
}

/** Which period a transition is about, in the shape the API takes. */
export function periodTarget(sheet: Timesheet, personId?: string) {
  return sheet.id
    ? { timesheetId: sheet.id }
    : { personId: personId ?? sheet.person.id, periodKind: sheet.periodKind, periodStart: sheet.periodStart };
}
